import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ClipboardList, X, Search, MapPin, Phone as PhoneIcon, RotateCcw, Ban, DollarSign } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { useToast } from './ui/Toast';
import apiClient from '../api/client';
import { ORDER_STATUSES, STATUS_LABELS, STATUS_COLORS, OrderStatus } from '../constants/orderStatus';

const PAGE_SIZE = 20;

const OrderManager = () => {
  const { showToast } = useToast();
  const [orders, setOrders] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  // Request generation: only the newest fetch may write rows/totals/loading,
  // so a slow response for an older filter can never replace the current one.
  const fetchSeq = useRef(0);

  const fetchOrders = useCallback(async () => {
    const seq = ++fetchSeq.current;
    setLoading(true);
    try {
      const res = await apiClient.get('/orders', {
        params: {
          status: statusFilter || undefined,
          search: searchTerm || undefined,
          page,
          limit: PAGE_SIZE
        }
      });
      if (seq !== fetchSeq.current) return;
      setOrders(res.data.orders);
      setTotal(res.data.total);
      setTotalPages(res.data.totalPages);
    } catch (error: any) {
      if (seq !== fetchSeq.current) return;
      showToast('Error al cargar pedidos', 'error');
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, searchTerm, page]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1);
      setSearchTerm(searchInput.trim());
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  const updateStatus = async (id: string, status: OrderStatus) => {
    try {
      await apiClient.patch(`/orders/${id}/status`, { status });
      showToast('Pedido actualizado', 'success');
      setSelectedOrder(null);
      fetchOrders();
    } catch (error) {
      showToast('Error al actualizar el pedido', 'error');
    }
  };

  const handleCancel = (order: any) => {
    if (!window.confirm('¿Estás seguro de cancelar este pedido?')) return;
    updateStatus(order._id, 'cancelado');
  };

  const handleRetryDelivery = async (order: any) => {
    if (!window.confirm('¿Reintentar este pedido? Volverá a la cocina como pendiente.')) return;
    try {
      await apiClient.patch(`/orders/${order._id}/retry-delivery`, { reason: 'Reintento desde admin' });
      showToast('Pedido reinsertado en cocina', 'success');
      setSelectedOrder(null);
      fetchOrders();
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Error al reintentar el pedido', 'error');
    }
  };

  const handleCollectPayment = async (order: any) => {
    let reference: string | undefined;
    if (order.paymentMethod === 'Transferencia') {
      reference = window.prompt('Referencia de la transferencia (verificada):') || undefined;
      if (!reference) return;
    } else if (!window.confirm(`¿Confirmar cobro de $${(order.totalAmount || 0).toLocaleString()} en ${order.paymentMethod}?`)) {
      return;
    }
    try {
      await apiClient.patch(`/orders/${order._id}/collect`, { reference });
      showToast('Cobro registrado', 'success');
      setSelectedOrder(null);
      fetchOrders();
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Error al registrar el cobro', 'error');
    }
  };

  const handleRefundPayment = async (order: any) => {
    const reason = window.prompt('Motivo del reembolso:') || '';
    if (!reason.trim()) return;
    try {
      await apiClient.patch(`/orders/${order._id}/refund`, { reason });
      showToast('Reembolso registrado', 'success');
      setSelectedOrder(null);
      fetchOrders();
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Error al registrar el reembolso', 'error');
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h2 className="text-2xl font-black flex items-center gap-3">
          <ClipboardList className="text-primary" />
          Pedidos
        </h2>
        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          <div className="relative">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              aria-label="Buscar pedidos por número, cliente o mesa"
              placeholder="Buscar por # de pedido, cliente o mesa..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              className="w-full sm:w-64 bg-slate-100 dark:bg-slate-900 border-none rounded-2xl py-3 pl-11 pr-4 focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <select
            aria-label="Filtrar pedidos por estado"
            value={statusFilter}
            onChange={e => { setPage(1); setStatusFilter(e.target.value); }}
            className="bg-slate-100 dark:bg-slate-900 border-none rounded-2xl py-3 px-4 focus:ring-2 focus:ring-primary/20"
          >
            <option value="">Todos los estados</option>
            {ORDER_STATUSES.map(s => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
          </select>
        </div>
      </div>

      <Card className="p-8">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="text-slate-400 text-xs uppercase tracking-widest border-b border-slate-100 dark:border-slate-800">
                <th className="pb-4 font-black">#</th>
                <th className="pb-4 font-black">Cliente / Mesa</th>
                <th className="pb-4 font-black">Fecha</th>
                <th className="pb-4 font-black">Total</th>
                <th className="pb-4 font-black">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 dark:divide-slate-900">
              {orders.map((order: any) => (
                <tr
                  key={order._id}
                  onClick={() => setSelectedOrder(order)}
                  className="text-slate-700 dark:text-slate-300 group hover:bg-slate-50 dark:hover:bg-slate-900/50 transition-colors cursor-pointer"
                >
                  <td className="py-5 font-black text-primary">{order.orderNumber ? `#${order.orderNumber}` : '—'}</td>
                  <td className="py-5 font-bold">
                    {order.delivery?.customerName || order.tableNumber || order.customerName || 'Sin nombre'}
                    {order.delivery && <span className="ml-2 text-[10px] font-black uppercase text-primary">Delivery</span>}
                  </td>
                  <td className="py-5 font-medium text-sm">{new Date(order.createdAt).toLocaleString()}</td>
                  <td className="py-5 font-medium">${(order.totalAmount || 0).toLocaleString()}</td>
                  <td className="py-5">
                    <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider ${STATUS_COLORS[order.status as OrderStatus]}`}>
                      {STATUS_LABELS[order.status as OrderStatus] || order.status}
                    </span>
                  </td>
                </tr>
              ))}
              {!loading && orders.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-10 text-center text-slate-400 font-medium">No se encontraron pedidos.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex justify-between items-center mt-6">
            <p className="text-sm text-slate-400 font-medium">{total} pedidos en total</p>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Anterior</Button>
              <span className="text-sm font-bold">{page} / {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Siguiente</Button>
            </div>
          </div>
        )}
      </Card>

      <AnimatePresence>
        {selectedOrder && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedOrder(null)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative bg-white dark:bg-slate-950 w-full max-w-2xl rounded-[32px] shadow-2xl overflow-hidden max-h-[85vh] flex flex-col"
            >
              <div className="p-8 overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    {selectedOrder.orderNumber && (
                      <p className="text-sm font-black text-primary mb-0.5">#{selectedOrder.orderNumber}</p>
                    )}
                    <h2 className="text-2xl font-black">
                      {selectedOrder.delivery?.customerName || selectedOrder.tableNumber || selectedOrder.customerName || 'Pedido'}
                    </h2>
                    <p className="text-sm text-slate-400 font-medium">{new Date(selectedOrder.createdAt).toLocaleString()}</p>
                  </div>
                  <Button type="button" variant="ghost" size="icon" onClick={() => setSelectedOrder(null)}>
                    <X size={24} />
                  </Button>
                </div>

                <span className={`inline-block px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider mb-6 ${STATUS_COLORS[selectedOrder.status as OrderStatus]}`}>
                  {STATUS_LABELS[selectedOrder.status as OrderStatus] || selectedOrder.status}
                </span>

                {selectedOrder.delivery && (
                  <div className="mb-6 space-y-1.5 bg-slate-50 dark:bg-slate-900/60 p-4 rounded-2xl">
                    <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                      <PhoneIcon size={14} />
                      <span className="text-sm font-bold">{selectedOrder.delivery.phone}</span>
                    </div>
                    <div className="flex items-start gap-2 text-slate-500 dark:text-slate-400">
                      <MapPin size={14} className="mt-0.5" />
                      <span className="text-sm">{selectedOrder.delivery.address}</span>
                    </div>
                    {selectedOrder.delivery.addressNotes && (
                      <p className="text-xs text-slate-400 italic pl-5">{selectedOrder.delivery.addressNotes}</p>
                    )}
                  </div>
                )}

                <div className="space-y-3 mb-6">
                  {selectedOrder.items?.map((item: any, idx: number) => {
                    const selections = item.selectedOptions instanceof Map
                      ? Object.fromEntries(item.selectedOptions)
                      : (item.selectedOptions || {});
                    return (
                      <div key={idx} className="flex items-start justify-between rounded-xl bg-slate-50 dark:bg-slate-900/60 p-4 border border-slate-100 dark:border-slate-800">
                        <div>
                          <p className="font-bold text-slate-700 dark:text-slate-300">
                            {item.quantity}x {item.name || item.product?.name || 'Producto eliminado'}
                          </p>
                          {Object.entries(selections).length > 0 && (
                            <p className="text-xs text-slate-400 mt-1">
                              {Object.values(selections).join(', ')}
                            </p>
                          )}
                        </div>
                        <p className="font-bold text-slate-700 dark:text-slate-300">${(item.price * item.quantity).toLocaleString()}</p>
                      </div>
                    );
                  })}
                </div>

                <div className="flex justify-between items-center border-t border-slate-100 dark:border-slate-800 pt-4 mb-6">
                  <span className="text-sm font-bold text-slate-500 uppercase">{selectedOrder.paymentMethod}</span>
                  <span className="text-2xl font-black">${(selectedOrder.totalAmount || 0).toLocaleString()}</span>
                </div>

                <div className="flex gap-3">
                  {selectedOrder.delivery && selectedOrder.status === 'entregado' && (
                    <Button
                      onClick={() => handleRetryDelivery(selectedOrder)}
                      className="flex-1 h-12 rounded-2xl font-black flex items-center justify-center gap-2"
                    >
                      <RotateCcw size={18} />
                      Reintentar entrega
                    </Button>
                  )}
                  {selectedOrder.status !== 'entregado' && selectedOrder.status !== 'cancelado' && (
                    <Button
                      variant="ghost"
                      onClick={() => handleCancel(selectedOrder)}
                      className="flex-1 h-12 rounded-2xl font-black flex items-center justify-center gap-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <Ban size={18} />
                      Cancelar pedido
                    </Button>
                  )}
                </div>

                <div className="flex gap-3 pt-1">
                  {selectedOrder.status !== 'cancelado' && selectedOrder.paymentStatus === 'unpaid' && (
                    <Button
                      onClick={() => handleCollectPayment(selectedOrder)}
                      className="flex-1 h-12 rounded-2xl font-black flex items-center justify-center gap-2"
                    >
                      <DollarSign size={18} />
                      Registrar cobro
                    </Button>
                  )}
                  {selectedOrder.paymentStatus === 'collected' && (
                    <Button
                      variant="ghost"
                      onClick={() => handleRefundPayment(selectedOrder)}
                      className="flex-1 h-12 rounded-2xl font-black flex items-center justify-center gap-2 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                    >
                      <RotateCcw size={18} />
                      Reembolsar
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default OrderManager;
